/**
 * DQ Workbench - Overview tab.
 *
 * Three regions: (1) four cadence cards (what to review, by check cadence),
 * (2) a "cleared this week" card with sync-confirmed stat tiles + a recent
 * team-activity feed, and (3) a warm "getting noisy" trending peek. The model
 * is read-first: people review/note/block, the next sync re-checks and
 * flips status to Resolved.
 */
import React, { useCallback, useMemo } from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'

import { useDataExceptionsCtx } from '../DataExceptionsContext'
import {
  dataExceptionsApi,
  type CadenceBucketsResponse,
  type CadenceBucketKey,
  type ExceptionSummary,
  type TrendingResponse,
} from '../../../../services/admin/dataExceptionsService'
import {
  exceptionActivityApi,
  type ExceptionActivityListResponse,
} from '../../../../services/exceptionActivityApi'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import DataFetchWrapper from '../../../../components/inspector/shared/DataFetchWrapper'
import { poolwerxColors } from '../../../../theme/colors'
import { dqTrendingWarm } from '../../../../theme/dqStatusColors'
import { cardChrome, SectionLabel } from '../components/chrome'
import CadenceCard from '../components/CadenceCard'
import ClearedTile from '../components/ClearedTile'
import ActivityFeed from '../components/ActivityFeed'
import TrendingPeek from '../components/TrendingPeek'
import AdminExceptionDrawerHost, {
  useAdminExceptionDrawer,
} from '../components/AdminExceptionDrawerHost'

const BASE = '/admin/data-exceptions'

const CADENCE_ORDER: { key: CadenceBucketKey; label: string }[] = [
  { key: 'due_today', label: 'Due today' },
  { key: 'this_week', label: 'This week' },
  { key: 'this_month', label: 'This month' },
  { key: 'backlog', label: 'Backlog' },
]

const Overview: React.FC = () => {
  const { market, refreshKey, refresh } = useDataExceptionsCtx()
  const drawer = useAdminExceptionDrawer()
  const scope = useMemo(() => (market ? { market } : {}), [market])
  const linkSearch = market ? `?market=${encodeURIComponent(market)}` : ''
  const gridTo = `${BASE}/grid${linkSearch}`
  const trendingTo = `${BASE}/trending${linkSearch}`

  const cadence = useDataFetching<CadenceBucketsResponse>({
    fetchFn: useCallback(() => dataExceptionsApi.getCadenceBuckets(scope), [scope]),
    dependencies: [market, refreshKey],
  })
  const summary = useDataFetching<ExceptionSummary>({
    fetchFn: useCallback(() => dataExceptionsApi.getSummary(scope), [scope]),
    dependencies: [market, refreshKey],
  })
  const activity = useDataFetching<ExceptionActivityListResponse>({
    fetchFn: useCallback(
      () =>
        exceptionActivityApi.getList({
          tab: 'all',
          page: 1,
          page_size: 8,
          sort_by: 'last_activity_at',
          sort_dir: 'desc',
          market: market || undefined,
        }),
      [market],
    ),
    dependencies: [market, refreshKey],
  })
  const trending = useDataFetching<TrendingResponse>({
    fetchFn: useCallback(
      () => dataExceptionsApi.getTrending({ ...scope, window: '7d', metric: 'new' }),
      [scope],
    ),
    dependencies: [market, refreshKey],
  })

  const bucketByKey = useMemo(() => {
    const map = new Map<CadenceBucketKey, CadenceBucketsResponse['buckets'][number]>()
    cadence.data?.buckets.forEach((b) => map.set(b.key, b))
    return map
  }, [cadence.data])

  const attribution = summary.data?.resolution_attribution

  return (
    <Box>
      {/* Region 1 - cadence cards */}
      <Box sx={{ mb: 4 }}>
        <SectionLabel>
          To review, by check cadence - bars show how much the team has looked at this period
        </SectionLabel>
        <DataFetchWrapper loading={cadence.loading} error={cadence.error} data={cadence.data?.buckets}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {CADENCE_ORDER.map(({ key, label }) => {
              const bucket = bucketByKey.get(key) ?? { key, total: 0, reviewed: 0, not_looked: 0 }
              return <CadenceCard key={key} bucket={bucket} label={label} to={gridTo} />
            })}
          </Box>
        </DataFetchWrapper>
      </Box>

      {/* Regions 2 + 3 */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Region 2 - cleared this week + activity */}
        <Box sx={{ flex: 1.4, minWidth: 0 }}>
          <SectionLabel>Cleared this week</SectionLabel>
          <Card sx={{ ...cardChrome }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Cleared this week - confirmed by sync
              </Typography>
              <DataFetchWrapper
                loading={summary.loading}
                error={summary.error}
                data={attribution ? [attribution] : null}
              >
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <ClearedTile
                    label="After Evo action"
                    value={attribution?.after_evo_action ?? 0}
                    caption="our fixes confirmed"
                    valueColor={poolwerxColors.darkCyan}
                  />
                  <ClearedTile
                    label="Silently"
                    value={attribution?.silent ?? 0}
                    caption="cleared, no touch"
                    valueColor={poolwerxColors.grey[600]}
                  />
                  {/* Room intentionally left for a future third tile. */}
                  <Box sx={{ flex: 1, minWidth: 0 }} />
                </Box>
              </DataFetchWrapper>

              <SectionLabel>Recent team activity</SectionLabel>
              <DataFetchWrapper
                loading={activity.loading}
                error={activity.error}
                data={activity.data?.items}
              >
                <ActivityFeed items={activity.data?.items ?? []} onSelect={drawer.open} />
              </DataFetchWrapper>
            </CardContent>
          </Card>
        </Box>

        {/* Region 3 - trending peek */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SectionLabel>Getting noisy</SectionLabel>
          <Card sx={{ ...cardChrome, bgcolor: dqTrendingWarm.bg, borderColor: dqTrendingWarm.border }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Trending - getting noisy
              </Typography>
              <DataFetchWrapper
                loading={trending.loading}
                error={trending.error}
                data={trending.data?.by_fp}
              >
                <TrendingPeek rows={trending.data?.by_fp ?? []} trendingTo={trendingTo} />
              </DataFetchWrapper>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 4 }}>
        Nothing here is resolved by a person — the team reviews, notes, blocks, or marks fixed; the
        next sync re-checks and flips status to Resolved.
      </Typography>

      <AdminExceptionDrawerHost
        selected={drawer.selected}
        onClose={drawer.close}
        onAfterMutation={refresh}
      />
    </Box>
  )
}

export default Overview
